import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { BsEyeFill } from 'react-icons/bs';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import MainCard from 'components/MainCard';
import { useQuery } from '@tanstack/react-query';
import { getAssignedStudents } from 'services/course/getAssignedStudents';

export default function AssignedStudents() {
  const navigate = useNavigate();

  // Get courseId from route state or fallback (adjust if needed)
  const { courseId } = useParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['assignedStudents', courseId],
    queryFn: () => getAssignedStudents({ courseId }),
    staleTime: 5000
  });

  const students = useMemo(() => {
    const assigned = data?.data?.data || [];

    return assigned.map((student) => ({
      studentId: student.studentId,
      studentName: student.fullName || 'N/A',
      studentEmail: student.email || 'N/A',
      studentContact: student.phone || 'N/A',
      lastActive: student.assignedAt ? new Date(student.assignedAt).toLocaleDateString() : 'N/A',
      studentStatus: student.status ?? 1
    }));
  }, [data]);

  const allCount = students.length;
  const activeCount = students.filter((s) => s.studentStatus === 1).length;
  const inactiveCount = students.filter((s) => s.studentStatus === 2).length;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredByTab = useMemo(() => {
    if (tabValue === 1) return students.filter((s) => s.studentStatus === 1);
    if (tabValue === 2) return students.filter((s) => s.studentStatus === 2);
    return students;
  }, [tabValue, students]);

  const filteredStudents = useMemo(() => {
    return filteredByTab.filter((student) => student.studentName.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [filteredByTab, searchTerm]);

  return (
    <MainCard>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary">
          Assigned Students
        </Typography>

        <Button
          variant="outlined"
          startIcon={<ArrowLeftOutlined />}
          onClick={() => navigate('/instructor')}
          sx={{ mr: 2, borderColor: 'primary.main', color: 'primary.main' }}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" size="small" />} iconPosition="end" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField label="Search Student" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ minWidth: 200 }} />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }} aria-label="assigned students table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Assigned On</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length > 0 ? (
              filteredStudents.map((student, index) => (
                <TableRow key={student.studentId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{student.studentName}</TableCell>
                  <TableCell>{student.studentEmail}</TableCell>
                  <TableCell>{student.studentContact}</TableCell>
                  <TableCell>{student.lastActive}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.studentStatus === 1 ? 'Active' : 'Inactive'}
                      color={student.studentStatus === 1 ? 'success' : 'error'}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ mt: 2 }}>{'No assigned students found.'}</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );
}
